import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';

import { properties } from '../../../environments/properties';
import { OpenDialog } from '../../utils/openDialog';
import { AppModalAdministratorSitaService } from './app-administrator-airport-sita.service';
import { AppModalDeletedSitaComponent } from './app-modal-deleted-sita/app-modal-deleted-sita.component';
import { Administrator } from './model/Administrator';
import { Airport } from './model/Airport';
import { AirportSita } from './model/AirportSita';
import { FtpSita } from './model/FtpSita';

@Component({
  selector: 'app-administrator-airport-sita',
  templateUrl: './app-administrator-airport-sita.component.html',
  styleUrls: ['./app-administrator-airport-sita.component.css'],
  standalone: false,
})
export class AppAdminsitratorAirportSitaComponent implements OnInit {
  private readonly DEFAULT_ADMINISTRATOR_CODE = 18;
  private readonly AIRPORT_OPTION_PROPERTIES: Array<keyof Airport> = [
    'codeIATA',
    'codeICAO',
    'descriptionName',
    'host',
    'pathRPE',
    'pathStamp',
    'user',
  ];

  @ViewChild('addAirportDialog') addAirportDialog: TemplateRef<any>;
  @ViewChild('confirmSaveAirportDialog') confirmSaveAirportDialog: TemplateRef<any>;

  public displayedColumns: string[] = [
    'identification',
    'iataCode',
    'decription',
    'editOrDelete',
  ];
  public hide = true;
  public dataSourceAirportsAllow: MatTableDataSource<AirportSita> = new MatTableDataSource<AirportSita>([]);
  public administrator: Administrator = new Administrator();
  public airportRegister: Airport = new Airport();
  public airportComboOptions: Airport[] = [];
  public airportSelectedInModal: Airport = null;

  private airportList: Airport[] = [];
  private airportSitaList: AirportSita[] = [];
  public formAdministratorCrud = new UntypedFormGroup({
    administratorName: new UntypedFormControl({ value: '', disabled: true }),
  });

  public formAdministratorAirportCrud = new UntypedFormGroup({
    airportRegisterHostFTP: new UntypedFormControl({ value: '', disabled: true }),
    airportRegisterPathFTP: new UntypedFormControl({ value: '', disabled: true }),
    airportRegisterPathSeloFTP: new UntypedFormControl({ value: '', disabled: true }),
    airportRegisterUserFTP: new UntypedFormControl({ value: '', disabled: true }),
    airportRegisterSecretFTP: new UntypedFormControl({ value: '', disabled: true }),
  });

  private addAirportDialogRef: MatDialogRef<any>;

  constructor(
    private appModalAdministratorService: AppModalAdministratorSitaService,
    private openDialogUtils: OpenDialog,
    private dialog: MatDialog,
  ) { }

  ngOnInit(): void {
    this.loadInitialAdministrator();
    this.disableAdministratorAirportFields();
    this.loadFtpSita();
    this.getAirportAdministrator(this.administrator.identification);
  }

  private loadInitialAdministrator(): void {
    this.administrator = new Administrator();
    this.administrator.identification = this.DEFAULT_ADMINISTRATOR_CODE;
  }

  private getAirportAdministrator(administratorCode: number): void {
    this.appModalAdministratorService.getAdministratorAirport(administratorCode).subscribe(
      administrator => {
        this.administrator = administrator;
      }
    );

    this.appModalAdministratorService.getListAirport().subscribe(
      airports => {
        this.airportSitaList = airports ? airports : [];
        this.airportList = this.airportSitaList
          .filter(airportSita => airportSita && airportSita.airportCode)
          .map(airportSita => airportSita.airportCode);
        this.dataSourceAirportsAllow = new MatTableDataSource(this.airportSitaList);
      }
    );
  }

  private loadFtpSita(): void {
    this.appModalAdministratorService.getFtpSita().subscribe(
      ftpSita => {
        this.applyFtpSitaToAirportRegister(ftpSita ? ftpSita : new FtpSita());
        this.disableAdministratorAirportFields();
      },
      () => this.disableAdministratorAirportFields()
    );
  }

  private applyFtpSitaToAirportRegister(ftpSita: FtpSita): void {
    this.airportRegister.host = ftpSita.host;
    this.airportRegister.pathRPE = ftpSita.pathRPE;
    this.airportRegister.pathStamp = ftpSita.pathStamp;
    this.airportRegister.user = ftpSita.user;
    this.airportRegister.secret = ftpSita.secret;

    this.formAdministratorAirportCrud.patchValue({
      airportRegisterHostFTP: this.airportRegister.host,
      airportRegisterPathFTP: this.airportRegister.pathRPE,
      airportRegisterPathSeloFTP: this.airportRegister.pathStamp,
      airportRegisterUserFTP: this.airportRegister.user,
      airportRegisterSecretFTP: this.airportRegister.secret,
    }, { emitEvent: false });
  }

  private disableAdministratorAirportFields(): void {
    this.formAdministratorAirportCrud.disable({ emitEvent: false });
  }

  private fillAirportComboOptions(): void {
    this.appModalAdministratorService.getListAirportNotExistAdministrator().subscribe(
      airports => this.reloadAirportComboOptions(airports),
      () => {
        this.airportComboOptions = [];
      }
    );
  }

  private reloadAirportComboOptions(airportsNotLinked: Airport[]): void {
    this.airportComboOptions = this.removeDuplicatedAirports(airportsNotLinked || []);
  }

  private removeDuplicatedAirports(airports: Airport[]): Airport[] {
    return airports.filter((airport, index, allAirports) =>
      airport && allAirports.findIndex(currentAirport => this.isSameAirport(currentAirport, airport)) === index
    );
  }

  private isSameAirport(firstAirport: Airport, secondAirport: Airport): boolean {
    if (!firstAirport || !secondAirport) {
      return false;
    }

    const hasSameId = firstAirport.id != null && secondAirport.id != null && firstAirport.id === secondAirport.id;
    const hasSameIata = !!firstAirport.codeIATA && !!secondAirport.codeIATA && firstAirport.codeIATA === secondAirport.codeIATA;
    const hasSameIcao = !!firstAirport.codeICAO && !!secondAirport.codeICAO && firstAirport.codeICAO === secondAirport.codeICAO;

    return hasSameId || hasSameIata || hasSameIcao;
  }

  private isSameAirportSita(firstAirportSita: AirportSita, secondAirportSita: AirportSita): boolean {
    if (!firstAirportSita || !secondAirportSita) {
      return false;
    }

    const hasSameId = firstAirportSita.id != null && secondAirportSita.id != null && firstAirportSita.id === secondAirportSita.id;

    return hasSameId || this.isSameAirport(firstAirportSita.airportCode, secondAirportSita.airportCode);
  }

  private removeAirportInList(airportRemoved: AirportSita): void {
    const removedAirport = airportRemoved ? airportRemoved.airportCode : null;

    this.airportSitaList = this.airportSitaList
      .filter(airportSita => !this.isSameAirportSita(airportSita, airportRemoved));
    this.airportList = this.airportList
      .filter(airport => !this.isSameAirport(airport, removedAirport));

    this.dataSourceAirportsAllow = new MatTableDataSource(this.airportSitaList);
  }

  public deleteAirport(airportSelect: AirportSita): void {
    this.dialog.open(AppModalDeletedSitaComponent, {
      width: '450px',
      data: { title: properties.MODAL_TITLE_PERIOD_SELECT_ATENCION, message: properties.MODAL_REMOVE_AIRPORT }
    }).afterClosed().subscribe(response => {
      if (response === true) {
        this.removeAirportAdministrator(airportSelect);
      }
    });
  }

  private removeAirportAdministrator(airportSelect: AirportSita): void {
    this.appModalAdministratorService.removeAirportAdministrator(airportSelect).subscribe(
      () => {
        this.openDialog(
          properties.MODAL_TITLE_SUCESS,
          properties.MODAL_BODY_SUCESS_OPERATION
        );

        this.removeAirportInList(airportSelect);
      },
      () => {
        this.openDialog(
          properties.MODAL_TITLE_PERIOD_SELECT_ATENCION,
          properties.MODAL_CONTENT_ERROR_IN_GENERATED_SERVICE
        );
      }
    );
  }

  public createdNewAirport(): void {
    this.airportSelectedInModal = null;
    this.fillAirportComboOptions();

    this.addAirportDialogRef = this.dialog.open(this.addAirportDialog, {
      width: '600px',
      disableClose: true,
      autoFocus: true,
    });
  }

  public cancelAddAirport(): void {
    this.airportSelectedInModal = null;

    if (this.addAirportDialogRef) {
      this.addAirportDialogRef.close();
    }
  }

  public selectAirportInModal(airport: Airport): void {
    this.airportSelectedInModal = airport;
  }

  public requestSaveAirportInModal(): void {
    if (!this.airportSelectedInModal) {
      this.openDialog(
        properties.MODAL_TITLE_PERIOD_SELECT_ATENCION,
        properties.MODAL_CONTENT_NOT_VALID_AIRPORT_SELECT
      );
      return;
    }

    this.dialog.open(this.confirmSaveAirportDialog, {
      width: '450px',
      disableClose: true,
      autoFocus: true,
    }).afterClosed().subscribe(response => {
      if (response === true) {
        this.saveNewAirportFromModal();
      }
    });
  }

  public formatAirportOption(airport: Airport): string {
    if (!airport) {
      return '';
    }

    return this.AIRPORT_OPTION_PROPERTIES
      .map(property => airport[property])
      .filter(value => value != null && value !== '')
      .map(value => String(value))
      .join(' - ');
  }

  private saveNewAirport(): void {
    this.appModalAdministratorService.saveAirportAdministratorCode(this.airportSelectedInModal.id).subscribe(
      airportSita => {
        this.airportSitaList.push(airportSita);
        this.airportList.push(Object.assign(new Airport(), this.airportSelectedInModal));
        this.dataSourceAirportsAllow = new MatTableDataSource(this.airportSitaList);
        this.fillAirportComboOptions();
        this.airportSelectedInModal = null;

        if (this.addAirportDialogRef) {
          this.addAirportDialogRef.close();
        }
      },
      () => {
        this.openDialog(
          properties.MODAL_TITLE_PERIOD_SELECT_ATENCION,
          properties.MODAL_CONTENT_ERROR_IN_GENERATED_SERVICE
        );
      }
    );
  }

  private saveNewAirportFromModal(): void {
    this.saveNewAirport();
  }

  private openDialog(mdAdTitleParam: string, mdAdMessageParam: string): void {
    this.openDialogUtils.openDialog(mdAdTitleParam, mdAdMessageParam);
  }
}
